import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import firebase from 'firebase/app';

import { FirebaseUtilitiesService } from '../../services/firebase-utilities.service';
import { UtilsService } from '../../services/utils.service';
import { AuthService } from '../../services/auth.service';
import { PhiAccessAuditService } from '../../services/phi-access-audit.service';
import { UIMessagingService } from '../../services/uimessaging.service';
import { UserRoles } from '../../dictionaries/UserRoles';
import { ConfirmationDialogComponent } from '../ui/confirmation-dialog/confirmation-dialog.component';

export interface DialogData {
  DateOfBirth: string;
  FirstName: string;
  LastName: string;
  caseName: string;
  patientDocId: string;
  metadata?: any;
  userFacility?: string;
  ownerID?: string;
  patientPicture?: string;
}

@Component({
    selector: 'app-edit-client-form',
    templateUrl: './edit-client-form.component.html',
    styleUrls: ['./edit-client-form.component.scss'],
    standalone: false
})
export class EditClientFormComponent implements OnInit {
  clientForm: UntypedFormGroup;
  patientIDs: Array<{ number: string; facility: string; isExisting?: boolean }> = [];
  errorMessage: string = '';
  userFacility: string = '';
  isOwner: boolean = false;

  facilities: Array<{ id: string; name: string }> = [];

  // Store original contact information values to detect changes
  private originalContactInfo = {
    spouseName: '',
    cellPhone: '',
    email: ''
  };

  // Store original medical record numbers to detect changes
  private originalMedicalRecords: Array<{ number: string; facility: string; isExisting?: boolean }> = [];

  constructor(
    private utils_$: UtilsService,
    private firebase_utilities_$: FirebaseUtilitiesService,
    private auth_$: AuthService,
    private uiMessaging_$: UIMessagingService,
    private phiAudit: PhiAccessAuditService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<EditClientFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    // Store original contact information values
    this.originalContactInfo = {
      spouseName: this.data.metadata?.spouseName || '',
      cellPhone: this.data.metadata?.cellPhone || '',
      email: this.data.metadata?.email || ''
    };

    this.clientForm = new UntypedFormGroup({
      dateOfBirth: new UntypedFormControl(this.getValidDate(this.data.DateOfBirth)),
      firstName: new UntypedFormControl(this.data.FirstName),
      lastName: new UntypedFormControl(this.data.LastName),
      gender: new UntypedFormControl(this.data.metadata?.gender || ''),
      caseName: new UntypedFormControl(this.data.caseName),
      spouseName: new UntypedFormControl(this.data.metadata?.spouseName || ''),
      cellPhone: new UntypedFormControl(this.data.metadata?.cellPhone || ''),
      email: new UntypedFormControl(this.data.metadata?.email || '', [])
    });
  }

  async ngOnInit() {
    // Get user's facility from dialog data or auth service as fallback
    const userData = this.auth_$.userData.getValue() as any;
    this.userFacility = this.data.userFacility || userData.facility || '';
    this.isOwner = userData?.role === UserRoles.owner;

    // Load facilities from FirebaseUtilitiesService
    try {
      const ownerID = this.data.ownerID || userData.ownerID || userData.uid;
      const facilitiesData = await this.firebase_utilities_$.getFacilities();
      this.facilities = facilitiesData.map(facility => ({
        id: facility.id || facility.name?.toLowerCase().replace(/\s+/g, '_'),
        name: facility.name
      }));
    } catch (error) {
      console.error('Error loading facilities:', error);
      // Fallback to empty array if facilities can't be loaded
      this.facilities = [];
    }

    // Load existing medical record numbers from patient metadata
    this.loadExistingMedicalRecords();
  }

  private loadExistingMedicalRecords() {
    // Load medical records from metadata if they exist
    // Check both old and new field names for backward compatibility
    const medicalRecords = this.data.metadata?.medical_record_numbers || this.data.metadata?.medical_record_numbers;

    if (medicalRecords && medicalRecords.length > 0) {
      // Map the medical records from metadata
      this.patientIDs = medicalRecords.map(record => ({
        number: record.number || record.value, // Handle both old and new formats
        facility: record.facility,
        isExisting: true
      }));
    } else {
      // Fallback: Start with the primary medical record (caseName) if no metadata exists
      // Use the user's facility if available, otherwise leave empty for user to select
      this.patientIDs = [
        {
          number: this.data.caseName,
          facility: this.userFacility || '',
          isExisting: true // The primary caseName is considered existing
        }
      ];
    }

    // Store original medical records for change detection
    this.originalMedicalRecords = JSON.parse(JSON.stringify(this.patientIDs));
  }

  // Add new medical record number
  addMedicalRecordNumber() {
    this.patientIDs.push({ number: '', facility: '', isExisting: false });
    this.errorMessage = '';
  }

  // Force change detection for the form
  onFormFieldChange() {
    // This method can be called when form fields change to trigger change detection
    // Angular should automatically detect changes, but this can help in some cases
  }

  // Remove medical record number (must keep at least one)
  removeMedicalRecordNumber(index: number) {
    if (this.patientIDs.length <= 1) {
      this.errorMessage = 'At least one medical record number is required';
      return;
    }

    // Check if this is an existing medical record
    if (this.patientIDs[index].isExisting) {
      this.errorMessage = 'Cannot remove existing medical record numbers';
      return;
    }

    this.patientIDs.splice(index, 1);
    this.errorMessage = '';
  }

  // Check if a medical record can be removed
  canRemoveMedicalRecord(index: number): boolean {
    return this.patientIDs.length > 1 && !this.patientIDs[index].isExisting;
  }

  // Check if facility is already selected
  isFacilityAlreadySelected(facilityId: string, currentIndex: number): boolean {
    return this.patientIDs.some(
      (record, index) => index !== currentIndex && record.facility === facilityId && facilityId.trim() !== ''
    );
  }

  // Check if contact information has been modified
  isContactInfoModified(): boolean {
    const currentValues = this.clientForm.value;
    const spouseChanged =
      (currentValues.spouseName || '').trim() !== (this.originalContactInfo.spouseName || '').trim();
    const phoneChanged = (currentValues.cellPhone || '').trim() !== (this.originalContactInfo.cellPhone || '').trim();
    const emailChanged = (currentValues.email || '').trim() !== (this.originalContactInfo.email || '').trim();

    return spouseChanged || phoneChanged || emailChanged;
  }

  // Check if medical records have been modified
  isMedicalRecordsModified(): boolean {
    // Check if the number of records has changed
    if (this.patientIDs.length !== this.originalMedicalRecords.length) {
      return true;
    }

    // Check if any record has been modified
    for (let i = 0; i < this.patientIDs.length; i++) {
      const current = this.patientIDs[i];
      const original = this.originalMedicalRecords[i];

      if (!original || current.number !== original.number || current.facility !== original.facility) {
        return true;
      }
    }

    return false;
  }

  // Check if only editable medical records have been modified
  isEditableMedicalRecordsModified(): boolean {
    // Check if any new records have been added
    const newRecords = this.patientIDs.filter(record => !record.isExisting);
    const originalNewRecords = this.originalMedicalRecords.filter(record => !record.isExisting);

    if (newRecords.length !== originalNewRecords.length) {
      return true;
    }

    // Check if any new/editable record has been modified
    for (let i = 0; i < this.patientIDs.length; i++) {
      const current = this.patientIDs[i];

      // Only check modifications for non-existing (editable) records
      if (!current.isExisting) {
        const original = this.originalMedicalRecords[i];

        if (!original || current.number !== original.number || current.facility !== original.facility) {
          return true;
        }
      }
    }

    return false;
  }

  // Check if any editable form data has been modified
  isFormModified(): boolean {
    return this.isContactInfoModified() || this.isEditableMedicalRecordsModified();
  }

  // Check if the form can be saved
  canSaveForm(): boolean {
    // Don't allow saving if there are errors
    if (this.errorMessage) {
      return false;
    }

    // Basic validation: must have at least one medical record with both number and facility
    // Only validate editable records (existing records are already valid)
    const editableRecords = this.patientIDs.filter(record => !record.isExisting);
    const hasValidEditableRecords =
      editableRecords.length === 0 ||
      editableRecords.every(
        record => record.number && record.number.trim() !== '' && record.facility && record.facility.trim() !== ''
      );

    if (!hasValidEditableRecords) {
      return false;
    }

    // Enable saving if form has been modified OR if we want to allow saving even without changes
    const hasModifications = this.isFormModified();

    // For now, let's always allow saving if the form is valid (even without modifications)
    // This ensures users can save the form
    return hasModifications || true; // Change to just 'hasModifications' if you want to require changes
  }

  // Get available facilities for a specific index
  getAvailableFacilities(currentIndex: number): Array<{ id: string; name: string }> {
    const selectedFacilities = this.patientIDs
      .map((record, index) => (index !== currentIndex ? record.facility : null))
      .filter(facility => facility && facility.trim() !== '');

    const availableFacilities = this.facilities.filter(facility => !selectedFacilities.includes(facility.id));

    // Always include the currently selected facility for this record (if any)
    const currentRecord = this.patientIDs[currentIndex];
    if (currentRecord && currentRecord.facility) {
      const currentFacility = this.facilities.find(f => f.id === currentRecord.facility);
      if (currentFacility && !availableFacilities.find(f => f.id === currentFacility.id)) {
        availableFacilities.push(currentFacility);
      }
    }

    return availableFacilities;
  }

  // Handle facility selection change
  onFacilityChange(selectedFacilityId: string, currentIndex: number): void {
    this.errorMessage = '';

    // Validate that this facility isn't already selected
    if (this.isFacilityAlreadySelected(selectedFacilityId, currentIndex)) {
      this.errorMessage = 'This facility is already selected for another medical record number';
      this.patientIDs[currentIndex].facility = '';
      return;
    }
  }

  // Validate medical records
  private validateMedicalRecords(): boolean {
    // Check that at least one medical record exists
    if (this.patientIDs.length === 0) {
      this.errorMessage = 'At least one medical record number is required';
      return false;
    }

    // Check that all editable medical records have both number and facility
    for (let i = 0; i < this.patientIDs.length; i++) {
      const record = this.patientIDs[i];

      // Only validate editable records (existing records are already valid)
      if (!record.isExisting) {
        if (!record.number.trim()) {
          this.errorMessage = `Patient ID ${i + 1} is required`;
          return false;
        }
        if (!record.facility.trim()) {
          this.errorMessage = `Facility selection is required for medical record number ${i + 1}`;
          return false;
        }
      }
    }

    // Check for duplicate facilities (only among non-existing records or when adding new ones)
    const facilities = this.patientIDs
      .map(record => record.facility)
      .filter(facility => facility && facility.trim() !== '');
    const uniqueFacilities = [...new Set(facilities)];

    if (facilities.length !== uniqueFacilities.length) {
      this.errorMessage = 'Each medical record must use a different facility';
      return false;
    }

    return true;
  }

  async onSubmit() {
    if (!this.validateMedicalRecords()) {
      return;
    }

    const { spouseName, cellPhone, email } = this.clientForm.value;

    // Prepare a normalized array of valid medical records
    const validMedicalRecords = this.patientIDs
      .filter(record => record.number.trim() && record.facility)
      .map(record => ({
        facility: record.facility,
        value: record.number,
        isExisting: record.isExisting
      }));

    try {
      // If editing the first record and it is new, update just the case name first
      if (validMedicalRecords[0] && !this.patientIDs[0].isExisting) {
        await this.updateCaseNameOnly(this.data.patientDocId, validMedicalRecords[0].value);
      }

      // Get new patient IDs (non-existing records)
      const newPatientIDs = validMedicalRecords.filter(record => !record.isExisting);

      // Prepare folder data including ownership, with improved error checking
      const folderObjects = await Promise.all(
        newPatientIDs.map(async record => {
          const ownerID = await this.firebase_utilities_$.getFacilityOwnerID(record.facility);
          if (!ownerID) {
            throw new Error(`Owner ID not found for facility: ${record.facility}`);
          }
          return {
            caseName: this.data.caseName,
            patientID: record.value,
            facility: record.facility,
            isExisting: record.isExisting,
            ownerID
          };
        })
      );

      // Create folders (split try/catch for more focused debugging)
      try {
        await this.createUploadFolder(folderObjects);
      } catch (err) {
        console.error('[EditClientForm] Failed to create upload folders', { folderObjects, err });
        throw err;
      }

      try {
        await this.createSharedFolder(folderObjects);
      } catch (err) {
        console.error('[EditClientForm] Failed to create shared folders', { folderObjects, err });
        throw err;
      }

      // Prepare updated metadata object
      const metadataUpdate = {
        ...this.data.metadata,
        medical_record_numbers: validMedicalRecords,
        spouseName: spouseName || '',
        cellPhone: cellPhone || '',
        email: email || ''
      };

      try {
        await this.updateClientMetadata(this.data.patientDocId, metadataUpdate);
      } catch (err) {
        console.error('[EditClientForm] Failed to update client metadata', { metadataUpdate, err });
        throw err;
      }

      this.dialogRef.close(1);
    } catch (error) {
      // Improve feedback about possible undefined/invalid values
      this.errorMessage = 'Failed to update patient information. Please try again.';
      if (
        error &&
        typeof error === 'object' &&
        error.message &&
        error.message.includes('Unsupported field value')
      ) {
        console.error('[EditClientForm] Firestore document contained undefined or invalid values.', error);
        alert(
          `Firestore rejected a document because of undefined or invalid field values. ` +
          `Please check required fields, especially those related to folders (upload/shared_files).\n\n` +
          `Error detail: ${error.message}`
        );
      } else {
        console.error('[EditClientForm] Error updating patient:', error);
      }
    }
  }

  private async updateCaseNameOnly(patientDocId: string, newCaseName: string) {
    if (!patientDocId) {
      // Fallback: find by current caseName if no patientDocId
      const querySnapshot = await firebase
        .firestore()
        .collection('patients')
        .where('caseName', '==', this.data.caseName)
        .limit(1)
        .get();

      if (!querySnapshot.empty) {
        const docId = querySnapshot.docs[0].id;
        await firebase.firestore().collection('patients').doc(docId).update({
          caseName: newCaseName
        });
      }
    } else {
      await firebase.firestore().collection('patients').doc(patientDocId).update({
        caseName: newCaseName
      });
    }
  }

  private async createUploadFolder(
    folderObjects: Array<{
      caseName: string;
      patientID: string;
      facility: string;
      isExisting?: boolean;
      ownerID: string;
    }>
  ) {
    for (const folderObject of folderObjects) {
      const uploadFolder = await this.firebase_utilities_$.createFolder({
        folderId: `d_${folderObject.caseName}_${folderObject.patientID}_${folderObject.facility}`,
        type: 'folder',
        children: '',
        parentFolder: '',
        belongsTo: folderObject.caseName,
        forDelete: false,
        predefined: false,
        color: '#004f99',
        name: 'Upload Files to share',
        ownerID: folderObject.ownerID,
        upload_folder: true,
        shared_files: false,
        createdAt: new Date()
      });
      console.log('Upload folder created:', uploadFolder);
    }
  }

  private async createSharedFolder(
    folderObjects: Array<{
      caseName: string;
      patientID: string;
      facility: string;
      isExisting?: boolean;
      ownerID: string;
    }>
  ) {
    for (const folderObject of folderObjects) {
      const sharedFolder = await this.firebase_utilities_$.createFolder({
        folderId: `d_${folderObject.caseName}_${folderObject.patientID}_${folderObject.facility}`,
        type: 'folder',
        children: '',
        parentFolder: '',
        belongsTo: folderObject.caseName,
        forDelete: false,
        predefined: false,
        color: '#00745a',
        shared_files: true,
        name: 'Shared Files',
        ownerID: folderObject.ownerID,
        createdAt: new Date()
      });
      console.log('Shared folder created:', sharedFolder);
    }
  }

  private async updateClientMetadata(patientDocId: string, metadata: any) {
    if (!patientDocId) {
      // Fallback: find by caseName if no patientDocId
      const primaryMedicalRecord = this.patientIDs[0];
      const querySnapshot = await firebase
        .firestore()
        .collection('patients')
        .where('caseName', '==', primaryMedicalRecord.number)
        .limit(1)
        .get();

      if (!querySnapshot.empty) {
        await firebase.firestore().collection('patients').doc(querySnapshot.docs[0].id).update({ metadata });
      }
    } else {
      await firebase.firestore().collection('patients').doc(patientDocId).update({ metadata });
    }
  }

  deletePatient() {
    const name = `${this.data.FirstName} ${this.data.LastName}`.trim() || this.data.caseName;
    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Patient',
        message: `Delete patient "${name}"? This will permanently remove the patient record. This cannot be undone.`,
        btnOkText: 'DELETE',
        btnCancelText: 'CANCEL'
      }
    });
    confirmRef.afterClosed().subscribe(async result => {
      if (!result?.ok) return;
      try {
        // Patient document ID equals caseName (set at creation time in createPatientMedical).
        // Prefer the explicit patientDocId if provided, fall back to caseName as doc ID.
        // Never do a collection query here — the allow list rule requires ownerID match,
        // which won't hold for patients created by a sub-user (CLIENT_ADMIN).
        const docId = this.data.patientDocId || this.data.caseName;
        if (docId) {
          await firebase.firestore().collection('patients').doc(docId).delete();
          this.phiAudit.logPhiAccess('patient', docId, 'delete');
        }
        this.dialogRef.close('deleted');
      } catch (error) {
        console.error('[EditClientForm] Error deleting patient:', error);
        this.errorMessage = 'Failed to delete patient. Please try again.';
      }
    });
  }

  private getValidDate(dateValue: any): Date | null {
    if (!dateValue) {
      return null;
    }

    // If it's already a Date object and valid
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      return dateValue;
    }

    // If it's a string, try to parse it
    if (typeof dateValue === 'string' && dateValue.trim() !== '') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // If it's a number (timestamp)
    if (typeof dateValue === 'number' && !isNaN(dateValue)) {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    return null;
  }
}
